// src/components/SupplierApplicationModal.jsx
import React, { useState, useRef, useEffect } from "react";
import directus from "../lib/directus";
import { createItem, readSingleton } from '@directus/sdk';

const SupplierApplicationModal = ({
  isOpen,
  onClose,
  categories,
  applicationStatus,
  setApplicationStatus,
}) => {
  const [error, setError] = useState("");
  const [step, setStep] = useState("fee"); // fee | form
  const [requiresInvoice, setRequiresInvoice] = useState(false);
  const [hasConfirmedPayment, setHasConfirmedPayment] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const [paymentError, setPaymentError] = useState("");

  // 每次打开弹窗时重置到支付提醒步骤
  useEffect(() => {
    if (isOpen) {
      setStep("fee");
      setHasConfirmedPayment(false);
      setRequiresInvoice(false);
      setPaymentLoading(true);
      setPaymentError("");
      const load = async () => {
        try {
          const settings = await directus.request(
            readSingleton("payment_settings", {
              fields: [
                "corporate_account_name",
                "corporate_bank_name",
                "corporate_account_number",
                "personal_qr_file",
              ],
            })
          );
          const item = settings;
          if (!item) {
            throw new Error("未配置支付信息（payment_settings 为空）");
          }
          const fileId = typeof item.personal_qr_file === 'string' ? item.personal_qr_file : item?.personal_qr_file?.id;
          const personalQrUrl = fileId ? `${process.env.REACT_APP_DIRECTUS_URL}/assets/${fileId}` : "";
          setPaymentSettings({
            corpName: item.corporate_account_name || "",
            corpBank: item.corporate_bank_name || "",
            corpAccount: item.corporate_account_number || "",
            personalQrUrl,
          });
        } catch (err) {
          console.warn("payment_settings 读取失败：", err);
          setPaymentError(typeof err?.message === 'string' ? err.message : '支付信息读取失败');
          setPaymentSettings(null);
        } finally {
          setPaymentLoading(false);
        }
      };
      load();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    // 前端验证（可选）
    const requiredFields = [
      "companyName", "creditCode", "category", "establishDate",
      "registeredCapital", "legalRepresentative", "contactPerson",
      "phone","email", "address", "description", "achievements"
    ];

    for (const field of requiredFields) {
      const value = formData.get(field)?.toString().trim();
      if (!value) {
        setError("请填写所有必填项");
        return;
      }
    }

    const data = {
      company_name: formData.get("companyName").toString().trim(),
      credit_code: formData.get("creditCode").toString().trim(),
      category: formData.get("category").toString().trim(),
      establish_date: formData.get("establishDate").toString().trim(),
      registered_capital: formData.get("registeredCapital").toString().trim(),
      legal_representative: formData.get("legalRepresentative").toString().trim(),
      contact_person: formData.get("contactPerson").toString().trim(),
      phone: formData.get("phone").toString().trim(),
      email: formData.get("email").toString().trim(),
      address: formData.get("address").toString().trim(),
      description: formData.get("description").toString().trim(),
      achievements: formData.get("achievements").toString().trim(),
      // status: "pending",
    };

    // 获取文件
    // const files = formData.getAll("certificates").filter(file => file instanceof File && file.size > 0);
    // let fileIds = [];

    try {
      setApplicationStatus("submitting");
      setError("");

      // 上传文件
    //   if (files.length > 0) {
    //     const uploadPromises = files.map(file => directus.files.upload(file));
    //     const results = await Promise.all(uploadPromises);
    //     fileIds = results.map(res => res.data.id);
    //   }

      // 创建申请记录
      await directus.request(createItem("supplier_applications", data));
    //   await directus.items('supplier_applications').createOne({
    //     ...data,
    //     // certificates: fileIds.length > 0 ? fileIds : null,
    //   });

      setApplicationStatus("success");
    } catch (err) {
      console.error("提交失败:", err);
      const msg = err?.errors?.[0]?.message || "提交失败，请稍后重试";
      setError(msg);
      setApplicationStatus("error");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900">供应商入围申请表</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
            aria-label="关闭"
          >
            ×
          </button>
        </div>
        <div className="p-4">
          {applicationStatus === "idle" && step === "fee" && (
            <div className="space-y-4">
              <div className="bg-yellow-50 p-4 rounded-2xl">
                <h4 className="text-lg font-semibold text-yellow-900 mb-3">申请前请先支付技术服务费</h4>
                <p className="text-yellow-800 text-base">
                  为保障系统维护与资格审查，请先完成技术服务费<span className="inline-block mx-1 px-3 py-1 bg-red-600 text-white font-bold text-xl rounded-lg shadow-md">1880元</span>支付。<br />
                  支付时<span className="font-bold">必须填写备注：</span>
                  <span className="font-bold text-yellow-900">供应商报名服务费+公司名称</span>。<br />
                  <span className="font-bold">需要发票：</span>请使用对公转账；<span className="font-bold">无需发票：</span>可使用个人收款码。
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="border-2 border-gray-200 rounded-2xl p-3">
                  <h5 className="font-semibold text-gray-900 mb-2">个人收款码（无需发票请走此通道）</h5>
                  <div className="bg-gray-50 rounded-xl p-3 text-center min-h-64 flex flex-col justify-center">
                    {paymentSettings?.personalQrUrl ? (
                      <img
                        src={paymentSettings.personalQrUrl}
                        alt="个人收款二维码"
                        className="mx-auto max-h-56 object-contain"
                      />
                    ) : (
                      <p className="text-sm text-gray-500">二维码未配置，请联系管理员</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">转账备注：供应商报名服务费+公司名称</p>
                  </div>
                </div>

                <div className="border-2 border-gray-200 rounded-2xl p-3">
                  <h5 className="font-semibold text-gray-900 mb-2">企业对公账号信息（发票请走此通道）</h5>
                  <div className="bg-gray-50 rounded-xl p-3 min-h-64 flex flex-col justify-center">
                    {paymentSettings ? (
                      <>
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">账户名称</p>
                            <div className="text-lg font-bold text-blue-700 leading-tight break-all">{paymentSettings.corpName}</div>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">开户银行</p>
                            <div className="text-lg font-bold text-blue-700 leading-tight break-all">{paymentSettings.corpBank}</div>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 mb-1">账户号码</p>
                            <div className="text-base text-gray-900 font-mono tracking-wider bg-white px-3 py-2 rounded-lg border border-gray-200 break-all">{paymentSettings.corpAccount}</div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">对公账号信息未配置，请联系管理员</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">转账备注：供应商报名服务费+公司名称</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={requiresInvoice}
                    onChange={(e) => setRequiresInvoice(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">我需要开具发票（请选择对公转账）</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={hasConfirmedPayment}
                    onChange={(e) => setHasConfirmedPayment(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">
                    我已完成支付并在备注中填写：供应商报名服务费+公司名称
                  </span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={() => setStep("form")}
                  disabled={!hasConfirmedPayment || paymentLoading || !paymentSettings}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  已确认支付，继续填写申请
                </button>
              </div>
            </div>
          )}
          {applicationStatus === "idle" && step === "form" && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-blue-50 p-6 rounded-2xl mb-8">
                <h4 className="text-lg font-semibold text-blue-900 mb-3">申请须知</h4>
                <p className="text-blue-800 text-lg">
                  1. 请确保填写的信息真实有效，如有虚假信息将取消申请资格。<br />
                  2. 我们将在15个工作日内完成审核，并通过邮件通知审核结果。<br />
                  3. 审核通过将在官网公示；如未通过，将通过邮件说明原因。<br />
                  4. 已支付技术服务费后提交申请，后台人工确认收款并审核。
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-3">公司全称 *</label>
                  <input
                    type="text"
                    name="companyName"
                    required
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                    placeholder="请输入公司全称"
                  />
                </div>


                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-3">统一社会信用代码 *</label>
                  <input
                    type="text"
                    name="creditCode"
                    required
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                    placeholder="请输入统一社会信用代码"
                  />
                </div>


                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-3">主营产品/服务类别 *</label>
                  <select
                    name="category"
                    required
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                  >
                    <option value="">请选择类别</option>
                    {categories
                      .filter((cat) => cat !== "all")
                      .map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                  </select>
                </div>


                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-3">公司成立日期 *</label>
                  <input
                    type="date"
                    name="establishDate"
                    required
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                  />
                </div>


                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-3">注册资本 *</label>
                  <input
                    type="text"
                    name="registeredCapital"
                    required
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                    placeholder="请输入注册资本（万元）"
                  />
                </div>


                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-3">法定代表人 *</label>
                  <input
                    type="text"
                    name="legalRepresentative"
                    required
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                    placeholder="请输入法定代表人姓名"
                  />
                </div>


                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-3">联系人姓名 *</label>
                  <input
                    type="text"
                    name="contactPerson"
                    required
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                    placeholder="请输入联系人姓名"
                  />
                </div>

               <div>
                  <label className="block text-lg font-medium text-gray-700 mb-3">联系电话 *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                    placeholder="请输入联系电话"
                  />
                </div>


                <div className="md:col-span-2">
                  <label className="block text-lg font-medium text-gray-700 mb-3">电子邮箱 *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                    placeholder="请输入电子邮箱"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-lg font-medium text-gray-700 mb-3">公司地址 *</label>
                  <input
                    type="text"
                    name="address"
                    required
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                    placeholder="请输入公司详细地址"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-lg font-medium text-gray-700 mb-3">公司简介及主营业务 *</label>
                  <textarea
                    name="description"
                    required
                    rows={4}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                    placeholder="请详细介绍公司主营业务、产品特点、技术优势"
                  ></textarea>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-lg font-medium text-gray-700 mb-3">主要业绩案例 *</label>
                  <textarea
                    name="achievements"
                    required
                    rows={4}
                    className="w-full px-5 py-4 border-2 border-gray-300 rounded-2xl focus:ring-4 focus:ring-blue-200 focus:border-blue-600 transition-all duration-300 text-lg"
                    placeholder="请列举3-5个主要业绩案例，包括项目名称、规模、完成时间等"
                  ></textarea>
                </div>

              {/* 修复文件选择问题 */}
              {/* <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">资质证书（可选）</label>
                <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center relative">
                  <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-gray-500 mb-2">请上传营业执照、资质证书等相关文件</p>
                  <p className="text-xs text-gray-400 mb-4">支持 JPG/PNG/PDF，单个文件 ≤10MB</p> */}

                  {/* 隐藏的 file input */}
                  {/* <input
                    type="file"
                    ref={fileInputRef}
                    name="certificates"
                    multiple
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                  /> */}

                  {/* 可点击按钮 */}
                  {/* <button
                    type="button"
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors duration-300"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    选择文件
                  </button>
                </div>
              </div> */}

              {error && <p className="text-red-600 text-sm">{error}</p>}

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={applicationStatus === "submitting"}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {applicationStatus === "submitting" ? "提交中..." : "提交申请"}
                </button>
              </div>
            </div>
            </form>
          )}

          {applicationStatus === "submitting" && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">正在提交申请，请稍候...</p>
            </div>
          )}

          {applicationStatus === "success" && (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">申请已提交</h4>
              <p className="text-gray-600 mb-4">
                我们将在收到转账并完成人工审核后，审核通过的供应商将于官网公示。请保持联系方式畅通。
              </p>
              <button
                onClick={onClose}
                className="text-blue-600 hover:underline"
              >
                关闭窗口
              </button>
            </div>
          )}

          {applicationStatus === "error" && (
            <div className="text-center py-8">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={() => setApplicationStatus("idle")}
                className="text-blue-600 hover:underline"
              >
                重新填写
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierApplicationModal;